import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { InconsistencyFilters } from '@/api/queries/useGetAllSubsetInconsistencies';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface Props {
  matterId: string;
}

export const inconsistencyDownloadColumnOptions = [
  { label: 'ID', value: 'ID' },
  { label: 'Inconsistency', value: 'Inconsistency' },
  { label: 'Inconsistency Type', value: 'Inconsistency Type' },
  { label: 'Source', value: 'Source' },
  { label: 'Severity', value: 'Severity' },
  { label: 'Fact Document Name', value: 'Fact Document Name' },
  { label: 'Fact Source Text', value: 'Fact Source Text' },
];

export const useDownloadInconsistencies = ({ matterId }: Props) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadInconsistencies = async ({
    subsetId,
    format,
    title,
    columns,
    searchQuery,
    filters,
  }: {
    subsetId: string;
    format: string;
    title: string;
    columns: string[];
    searchQuery?: string;
    filters: InconsistencyFilters;
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        subsetId,
        format,
        title,
        columns,
        ...(searchQuery ? { searchQuery } : {}),
        ...filters,
      },
    });

    const response = await fetch(`${APIBaseChronos}/api/matter/${matterId}/inconsistency/download`, fetchConfig);

    if (!response.ok) {
      throw new Error('Failed to download inconsistencies');
    }

    const blob = await response.blob();

    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `${title}.${format}`,
    });

    link.click();

    URL.revokeObjectURL(link.href);

    trackCustomEvent(TrackingEventsV2.InconsistencyDownload);
  };

  const { mutate: fetchDownload, isLoading: isLoadingDownload } = useMutation(downloadInconsistencies, {
    onError: () => {
      toast.error('Failed to download inconsistencies. Please try again.');
    },
  });

  return { fetchDownload, isLoadingDownload };
};
